import { useEffect, useRef } from 'react';

import { Bell, CircleUserRound, MoonStar, Sun } from 'lucide-react';
import { useClerk } from '@clerk/clerk-react';
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { Breadcrumbs } from '~/components/common/Breadcrumbs';
import { LogoBrand } from '~/components/common';
import { TrialBanner } from '~/components/trial/TrialBanner';

import { cn } from '~/lib/utils';
import { useAuth } from '~/hooks/useAuth';
import { useUnreadCountQuery } from '~/hooks/useNotificationsQuery';

interface RootLayoutProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

// Subtle gradient background for brand warmth. Built from the shared brand
// tokens (--primary-muted, --info, --background) so it adapts to light/dark
// automatically.
const BRAND_GRADIENT_BACKGROUND =
  'radial-gradient(ellipse at 20% 0%, var(--primary-muted) 0%, transparent 50%), ' +
  'radial-gradient(ellipse at 80% 100%, color-mix(in srgb, var(--info) 8%, transparent) 0%, transparent 40%), ' +
  'var(--background)';

export function RootLayout({ darkMode, toggleDarkMode }: RootLayoutProps) {
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { user, isLoaded } = useAuth();
  const { data: unreadCount = 0 } = useUnreadCountQuery();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const isFirstRender = useRef(true);

  // Move focus to the main landmark on route change (not on first load, where
  // the browser's own initial focus is appropriate) so keyboard/AT users land
  // on the new page's content instead of staying on the stale focus target.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    mainRef.current?.focus();
  }, [location.pathname]);

  const handleProfileClick = () => {
    void navigate({ to: '/profile' });
  };

  const handleLogOutClick = () => {
    void signOut();
  };

  const handleNotificationsClick = () => {
    void navigate({ to: '/notifications' });
  };

  // No dedicated billing route exists; the Profile page surfaces the current
  // Usage Plan and usage stats, so upgrade/view-plans actions route there.
  const handleUpgrade = () => {
    void navigate({ to: '/profile' });
  };

  const handleViewPlans = () => {
    void navigate({ to: '/profile' });
  };

  // Extract trialEndsAt from user metadata (will be set by backend in task 3.3)
  // Hide banner until user data is loaded to prevent flash during initial load
  const trialEndsAt = isLoaded
    ? ((user?.publicMetadata?.trialEndsAt as string | undefined) ?? null)
    : null;

  const notificationsLabel =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

  return (
    <TooltipProvider>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        Skip to content
      </a>
      <div className="flex h-screen flex-col">
        <header className="sticky top-0 z-50 border-b border-border bg-card">
          <div className="flex min-h-16 items-center px-4">
            <Link to="/" className="mr-8 text-foreground no-underline hover:opacity-80">
              <LogoBrand size="medium" />
            </Link>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              aria-label="Toggle theme"
              className="mr-2 size-11 text-muted-foreground sm:size-9"
            >
              {darkMode ? <Sun /> : <MoonStar />}
            </Button>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleNotificationsClick}
                    aria-label={notificationsLabel}
                    className="relative mr-2 size-11 text-muted-foreground sm:size-9"
                  >
                    <Bell />
                    <Badge
                      aria-hidden="true"
                      data-testid="notifications-unread-badge"
                      className={cn(
                        'absolute -top-1 -right-1 h-5 min-w-5 justify-center px-1 text-[10px] tabular-nums',
                        unreadCount === 0 && 'invisible'
                      )}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  </Button>
                }
              />
              <TooltipContent>
                {unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
              </TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    id="account-button"
                    aria-label="Account menu"
                    className="size-11 text-muted-foreground sm:size-9"
                  >
                    <CircleUserRound />
                  </Button>
                }
              />
              <DropdownMenuContent id="account-menu" align="end" aria-labelledby="account-button">
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={handleProfileClick}>Profile</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogOutClick}>Log Out</DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {/* `relative` makes this scroll area the containing block for
            absolutely positioned descendants such as sr-only labels; without
            it they resolve against <body> and overflow the document, adding a
            second, window-level scrollbar. */}
        <div
          className="relative flex flex-1 justify-center overflow-auto [scrollbar-gutter:stable]"
          style={{ background: BRAND_GRADIENT_BACKGROUND }}
        >
          <main
            id="main"
            ref={mainRef}
            tabIndex={-1}
            className="mx-auto flex min-h-full w-full max-w-screen-lg flex-1 flex-col self-start px-4 pt-8 pb-24 outline-none"
          >
            <TrialBanner
              trialEndsAt={trialEndsAt}
              onUpgrade={handleUpgrade}
              onViewPlans={handleViewPlans}
            />
            <Breadcrumbs />
            <div className="mt-4 flex min-h-0 flex-1 flex-col">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
